import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

import { API_ENDPOINTS } from '../config/api';
import ConferenciaImportacao from './ConferenciaImportacao';

const LIMITE = 10 * 1024 * 1024;

const oQueEntrou = (importados = []) => {
  if (importados.includes('dre') && importados.includes('balanco')) return 'DRE e balanço foram importados.';
  if (importados.includes('dre')) return 'A DRE foi importada.';
  if (importados.includes('balanco')) return 'O balanço foi importado.';
  return 'A estrutura foi reconhecida.';
};

/**
 * A única porta de upload da interface.
 *
 * O servidor identifica a estrutura antes de escolher o parser. Modelos
 * conhecidos entram diretamente; arquivo externo devolve uma proposta e passa
 * pela conferência que já existia. O componente não tenta adivinhar pelo nome
 * do arquivo — nome não é estrutura.
 */
function ImportarPlanilhaLivre({ onImportado }) {
  const [arquivo, setArquivo] = useState(null);
  const [proposta, setProposta] = useState(null);
  const [identificacao, setIdentificacao] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);
  const [lendo, setLendo] = useState(false);
  const [aplicando, setAplicando] = useState(false);

  const aoEscolher = useCallback((aceitos, recusados) => {
    setErro(null);
    setResultado(null);
    const escolhido = aceitos?.[0];
    if (escolhido) {
      setArquivo(escolhido);
      return;
    }
    if (recusados?.length) {
      setArquivo(null);
      setErro('Envie um arquivo .xlsx, .xls ou .csv com até 10 MB.');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: aoEscolher,
    accept: {
      'application/vnd.ms-excel': ['.xls', '.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'application/csv': ['.csv'],
      'application/octet-stream': ['.xls', '.xlsx', '.csv']
    },
    maxSize: LIMITE,
    multiple: false,
    noClick: Boolean(arquivo)
  });

  const concluir = async (dados) => {
    await onImportado({
      dre: dados.dre || null,
      balanco: dados.balanco || null,
      identificacao: dados.identificacao,
      foraDaConta: dados.foraDaConta || null
    });
    setResultado(dados.identificacao);
    setArquivo(null);
  };

  const enviar = async () => {
    if (!arquivo) return;
    setErro(null);
    setLendo(true);
    try {
      const corpo = new FormData();
      corpo.append('file', arquivo);
      const resposta = await fetch(API_ENDPOINTS.UPLOAD_IDENTIFICAR, { method: 'POST', body: corpo });
      const dados = await resposta.json();
      if (!resposta.ok) throw new Error(dados.message || 'Não foi possível ler esta planilha.');

      setIdentificacao(dados.data.identificacao);
      if (dados.data.proposta) {
        setProposta(dados.data.proposta);
      } else {
        await concluir(dados.data);
      }
    } catch (e) {
      setErro(e.message);
    } finally {
      setLendo(false);
    }
  };

  const confirmar = async (confirmacoes) => {
    setAplicando(true);
    setErro(null);
    try {
      const resposta = await fetch(API_ENDPOINTS.UPLOAD_RECONHECER_APLICAR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposta, confirmacoes })
      });
      const dados = await resposta.json();
      if (!resposta.ok) throw new Error(dados.message || 'Não foi possível importar.');

      await concluir({
        dre: dados.data.dre,
        balanco: null,
        foraDaConta: dados.data.foraDaConta,
        identificacao: {
          ...identificacao,
          importados: ['dre'],
          exigeConferencia: false
        }
      });
      setProposta(null);
    } catch (e) {
      setErro(e.message);
    } finally {
      setAplicando(false);
    }
  };

  if (proposta) {
    return (
      <Box sx={{ mb: 3, textAlign: 'left' }}>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        <ConferenciaImportacao
          proposta={proposta}
          enviando={aplicando}
          onConfirmar={confirmar}
          onCancelar={() => {
            setProposta(null);
            setIdentificacao(null);
            setArquivo(null);
          }}
        />
      </Box>
    );
  }

  return (
    <Card sx={{ mb: 3, textAlign: 'left' }}>
      <CardContent>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 650 }}>
              Envie uma planilha
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '70ch' }}>
              Pode ser o modelo ConsultorIA, a DRE ou o balanço dos formatos já aceitos,
              ou outro relatório financeiro. Identificamos a estrutura e encaminhamos para a leitura certa.
            </Typography>
          </Box>

          <Box
            {...getRootProps()}
            sx={{
              border: '1px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              borderRadius: 2,
              minHeight: 176,
              px: { xs: 2, sm: 4 },
              py: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              cursor: arquivo ? 'default' : 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'transparent',
              transition: 'border-color 160ms ease, background-color 160ms ease',
              '&:hover': arquivo ? undefined : { borderColor: 'primary.main', bgcolor: 'action.hover' }
            }}
          >
            <input {...getInputProps()} />
            {arquivo ? (
              <Stack spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                <InsertDriveFileOutlinedIcon color="primary" sx={{ fontSize: 36 }} />
                <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap title={arquivo.name}>
                    {arquivo.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(arquivo.size / 1024).toFixed(1)} KB · confira antes de importar
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button variant="text" onClick={open} disabled={lendo}>
                    Trocar arquivo
                  </Button>
                  <Button
                    variant="contained"
                    onClick={enviar}
                    disabled={lendo}
                    startIcon={lendo ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlinedIcon />}
                  >
                    {lendo ? 'Identificando…' : 'Identificar e importar'}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={1} alignItems="center">
                <CloudUploadOutlinedIcon color="primary" sx={{ fontSize: 40 }} />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {isDragActive ? 'Solte o arquivo aqui' : 'Arraste ou clique para escolher'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  .xlsx, .xls ou .csv · até 10 MB
                </Typography>
              </Stack>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary">
            Nos demais formatos, compatibilizamos as linhas de DRE que reconhecemos e pedimos sua confirmação.
          </Typography>

          {erro && (
            <Alert severity="error">
              <AlertTitle>Não foi possível importar</AlertTitle>
              {erro}
            </Alert>
          )}

          {resultado && (
            <Alert severity="success">
              <AlertTitle>Estrutura reconhecida: {resultado.rotulo}</AlertTitle>
              {oQueEntrou(resultado.importados)} Você pode enviar outro arquivo neste mesmo campo.
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default ImportarPlanilhaLivre;
